import { useState, useEffect, useRef } from 'react';
import { useSearchStore } from '../../stores/search-store.js';
import { api } from '../../lib/api-client.js';
import { updateBarClause } from '../../lib/query-builder.js';

const inputClass =
  'bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors w-full';

export function TypeLineMultiSelect() {
  const types = useSearchStore((s) => s.advancedFilters.types);
  const setAdvancedFilter = useSearchStore((s) => s.setAdvancedFilter);
  const setUserInput = useSearchStore((s) => s.setUserInput);

  const syncTypesBar = (newTypes: string[]) => {
    // Remove all existing t: clauses, then append each selected type
    let bar = useSearchStore.getState().userInput;
    bar = updateBarClause(bar, 't:', null);
    for (const t of newTypes) {
      bar = bar ? `${bar} t:${t}` : `t:${t}`;
    }
    setUserInput(bar);
  };

  const [inputValue, setInputValue] = useState('');
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [filtered, setFiltered] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch types once
  useEffect(() => {
    let cancelled = false;
    api.getCatalogTypes().then((res) => {
      if (!cancelled) setAllTypes(res.types);
    }).catch(() => {/* ignore */});
    return () => { cancelled = true; };
  }, []);

  // Filter suggestions based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFiltered([]);
      return;
    }
    const q = inputValue.toLowerCase();
    const matches = allTypes
      .filter((t) => t.toLowerCase().includes(q) && !types.includes(t))
      .slice(0, 15);
    setFiltered(matches);
  }, [inputValue, allTypes, types]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addType = (t: string) => {
    const newTypes = [...types, t];
    setAdvancedFilter('types', newTypes);
    syncTypesBar(newTypes);
    setInputValue('');
    setIsOpen(false);
  };

  const removeType = (t: string) => {
    const newTypes = types.filter((x) => x !== t);
    setAdvancedFilter('types', newTypes);
    syncTypesBar(newTypes);
  };

  return (
    <div className="space-y-1.5" ref={containerRef}>
      <label className="text-[10px] text-gray-500 uppercase tracking-wider">Type Line</label>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search types..."
          className={inputClass}
        />
        {isOpen && filtered.length > 0 && (
          <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-40 overflow-y-auto">
            {filtered.map((t) => (
              <button
                key={t}
                onClick={() => addType(t)}
                className="block w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 transition-colors"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>
      {types.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => removeType(t)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-700 text-gray-300 border border-gray-600 hover:border-red-500 transition-colors"
            >
              {t}
              <span className="opacity-60 hover:opacity-100">x</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
